import React, { useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import HelpOutline from "@mui/icons-material/HelpOutline";
import ManageAccounts from "@mui/icons-material/ManageAccounts";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo"; // Icon for Webinar
import MyAccountDialog from "./MyAccountDialog";
import { useAuth } from "../context/AuthContext";
import { sendSupportEmailToAdmin } from "../networking/AccountCalls";

const RightDrawer = () => {
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false); // For video popup
  const [supportMessage, setSupportMessage] = useState("");
  const { logout, currentUser } = useAuth();

  // Firebase Storage URL for the webinar video
  const webinarVideoUrl =
    "https://firebasestorage.googleapis.com/v0/b/quantum-6c796.appspot.com/o/quantum_webinar_and_demo%20(360p).mp4?alt=media&token=05fcc348-2c2e-4c65-93b3-eb0db77884e7";

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setOpen(open);
  };

  const handleAccountClick = () => {
    setAccountDialogOpen(true);
  };

  const handleCloseAccountDialog = () => {
    setAccountDialogOpen(false);
  };

  const handleSignOutClick = () => {
    setDialogOpen(true);
  };

  const handleSignOutConfirm = () => {
    logout();
    setDialogOpen(false);
    setOpen(false);
  };

  const handleSignOutCancel = () => {
    setDialogOpen(false);
  };

  const handleSupportClick = () => {
    setSupportDialogOpen(true);
  };

  const handleSupportSend = async () => {
    if (!supportMessage.trim()) {
      alert("Please enter a message.");
      return;
    }
    try {
      await sendSupportEmailToAdmin(currentUser.email, supportMessage);
      alert("Support request sent successfully.");
      setSupportDialogOpen(false);
      setSupportMessage("");
    } catch (error) {
      console.error("Error sending support request:", error);
      alert("Failed to send support request.");
    }
  };

  const handleSupportCancel = () => {
    setSupportDialogOpen(false);
    setSupportMessage("");
  };

  const handleVideoClick = () => {
    setVideoDialogOpen(true);
    setOpen(false); // Close the drawer
  };

  const handleVideoDialogClose = () => {
    setVideoDialogOpen(false);
  };

  const list1 = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {/* Webinar ListItem (first item) */}
        <ListItem button onClick={handleVideoClick}>
          <ListItemIcon>
            <OndemandVideoIcon />
          </ListItemIcon>
          <ListItemText primary="Webinar" />
        </ListItem>

        {/* Existing ListItems */}
        <ListItem button onClick={handleAccountClick}>
          <ListItemIcon>
            <ManageAccounts />
          </ListItemIcon>
          <ListItemText primary="My Account" />
        </ListItem>
        <ListItem button onClick={handleSupportClick}>
          <ListItemIcon>
            <HelpOutline />
          </ListItemIcon>
          <ListItemText primary="Request Support" />
        </ListItem>
        <ListItem button onClick={handleSignOutClick}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Sign Out" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <div>
      <IconButton
        edge="end"
        color="inherit"
        aria-label="menu"
        onClick={toggleDrawer(true)}
      >
        <MenuIcon />
      </IconButton>
      <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
        {list1}
      </Drawer>

      {/* Confirmation Dialog for Sign Out */}
      <Dialog
        open={dialogOpen}
        onClose={handleSignOutCancel}
        aria-labelledby="sign-out-dialog-title"
        aria-describedby="sign-out-dialog-description"
      >
        <DialogTitle id="sign-out-dialog-title">{"Sign Out"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="sign-out-dialog-description">
            Are you sure you want to sign out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSignOutCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSignOutConfirm} color="primary" autoFocus>
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Support Request Dialog */}
      <Dialog
        open={supportDialogOpen}
        onClose={handleSupportCancel}
        aria-labelledby="support-dialog-title"
      >
        <DialogTitle id="support-dialog-title">{"Request Support"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter the details of your issue or question, and we will get
            back to you as soon as possible.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="supportMessage"
            label="Support Message"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={supportMessage}
            onChange={(e) => setSupportMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSupportCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSupportSend} color="primary">
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Viewer Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={handleVideoDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Quantum Webinar</DialogTitle>
        <DialogContent>
          <video
            controls
            style={{ width: "100%", maxHeight: "500px" }}
            src={webinarVideoUrl}
          >
            Your browser does not support the video tag.
          </video>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVideoDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* My Account Dialog */}
      <MyAccountDialog
        open={accountDialogOpen}
        onClose={handleCloseAccountDialog}
      />
    </div>
  );
};

export default RightDrawer;
